/**
 * `AssignedLocationsCell` (DNKADM-0078) — the assigned-Locations list-cell shared by
 * the Resources (`ResourcesScreen`) and Staff (`StaffScreen`) tables. It renders the
 * inline list-row `locations` summary (`HANDOFF-dink-api-list-row-locations`) in three
 * states, with NO per-row fetch (the summary rides on the row):
 *
 *  - a **non-empty** array → one location **name per line** (the row grows taller with
 *    multiple; no chips, Operator-specified);
 *  - an **empty** array `[]` → the text **"No locations"** in the theme error color
 *    (`error.main`) — a real "assigned to no locations" signal;
 *  - **`undefined`** (the field is not emitted yet) → a neutral **"—"**
 *    (`text.secondary`), NEVER the red empty state. Absent ≠ empty — this is the
 *    forward-compatible degrade until the backend field ships.
 */
import { Box, Typography } from '@mui/material';
import type { LocationSummary } from '@/api/locations';

export function AssignedLocationsCell({
  locations,
}: {
  /** The row's inline summary; `undefined` = field absent (renders "—"). */
  locations: LocationSummary[] | undefined;
}) {
  // Absent (unknown) — never the red empty state.
  if (locations === undefined) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        —
      </Typography>
    );
  }
  // Explicit empty — assigned to no locations.
  if (locations.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'error.main' }}>
        No locations
      </Typography>
    );
  }
  // One name per line (the row grows naturally with multiple).
  return (
    <Box>
      {locations.map((location) => (
        <Typography key={location.id} variant="body2">
          {location.name}
        </Typography>
      ))}
    </Box>
  );
}
